import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Alert,
    Container,
    CssBaseline,
    CircularProgress,
    LinearProgress,
    Link
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import logo from '../../images/cardireach_logo.jpeg';

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const [token, setToken] = useState('');
    const [userId, setUserId] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isValidatingToken, setIsValidatingToken] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const navigate = useNavigate();

    // Password strength indicator
    const [passwordStrength, setPasswordStrength] = useState(0);

    // Helper function to parse error messages from backend responses
    const parseErrorMessage = (errorData: any, defaultMessage: string): string => {
        if (!errorData || !errorData.detail) {
            return defaultMessage;
        }

        const detail = errorData.detail;

        // If detail is a string, return it directly
        if (typeof detail === 'string') {
            return detail;
        }

        // If detail is an array of validation errors
        if (Array.isArray(detail) && detail.length > 0) {
            const firstError = detail[0];
            return firstError.msg || defaultMessage;
        }

        return defaultMessage;
    };

    useEffect(() => {
        // Parse token and userId from URL
        const tokenFromUrl = searchParams.get('token');
        const userIdFromUrl = searchParams.get('userId') || searchParams.get('user_id');

        if (tokenFromUrl && userIdFromUrl) {
            setToken(tokenFromUrl);
            setUserId(userIdFromUrl);
            validateToken(tokenFromUrl, userIdFromUrl);
        } else {
            const missing = [];
            if (!tokenFromUrl) missing.push('token');
            if (!userIdFromUrl) missing.push('userId');
            setError(`Invalid or missing reset parameters: ${missing.join(', ')}`);
            setIsValidatingToken(false);
        }
    }, [searchParams]);

    const validateToken = async (tokenToValidate: string, userIdToValidate: string) => {
        try {
            const API_URL = process.env.REACT_APP_API_URL;
            const response = await fetch(`${API_URL}/auth/validate-reset-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token: tokenToValidate,
                    user_id: userIdToValidate
                }),
            });

            if (response.ok) {
                setTokenValid(true);
            } else {
                // Parse error response
                try {
                    const errorData = await response.json();
                    const errorMessage = parseErrorMessage(
                        errorData,
                        'Invalid or expired reset token. Please request a new password reset link.'
                    );
                    setError(errorMessage);
                } catch (parseError) {
                    setError('Invalid or expired reset token. Please request a new password reset link.');
                }
                setTokenValid(false);
            }
        } catch (err) {
            console.error('Token validation error:', err);
            setError('Failed to validate reset token. Please try again.');
            setTokenValid(false);
        } finally {
            setIsValidatingToken(false);
        }
    };

    const calculatePasswordStrength = (password: string): number => {
        let strength = 0;
        if (password.length >= 8) strength += 25;
        if (password.length >= 12) strength += 25;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
        if (/\d/.test(password)) strength += 12.5;
        if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 12.5;
        return Math.min(strength, 100);
    };

    const handlePasswordChange = (password: string) => {
        setNewPassword(password);
        setPasswordStrength(calculatePasswordStrength(password));
    };

    const getPasswordStrengthColor = () => {
        if (passwordStrength < 40) return 'error';
        if (passwordStrength < 70) return 'warning';
        return 'success';
    };

    const getPasswordStrengthLabel = () => {
        if (passwordStrength < 40) return 'Weak';
        if (passwordStrength < 70) return 'Medium';
        return 'Strong';
    };

    const validatePassword = (): boolean => {
        if (!newPassword || !confirmPassword) {
            setError('Please enter both password fields');
            return false;
        }

        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters long');
            return false;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!validatePassword()) {
            return;
        }

        setIsLoading(true);

        try {
            const API_URL = process.env.REACT_APP_API_URL;

            // Reset password with token, user_id, and new password
            const response = await fetch(`${API_URL}/auth/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token,
                    user_id: userId,
                    password: newPassword,
                }),
            });

            if (response.ok) {
                setSuccess('Password reset successful! Redirecting to login...');
                setNewPassword('');
                setConfirmPassword('');

                // Redirect to login after 2 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            } else {
                const errorData = await response.json();
                const errorMessage = parseErrorMessage(
                    errorData,
                    'Failed to reset password. Please try again.'
                );
                setError(errorMessage);
            }
        } catch (err) {
            console.error('Reset password error:', err);
            setError('An error occurred. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    };

    if (isValidatingToken) {
        return (
            <Container component="main" maxWidth="xs">
                <CssBaseline />
                <Box
                    sx={{
                        marginTop: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Validating reset token...</Typography>
                </Box>
            </Container>
        );
    }

    if (!tokenValid) {
        return (
            <Container component="main" maxWidth="xs">
                <CssBaseline />
                <Box
                    sx={{
                        marginTop: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        component="img"
                        src={logo}
                        alt="Cardireach Logo"
                        sx={{
                            width: 120,
                            height: 120,
                            objectFit: 'contain',
                            mb: 2
                        }}
                    />

                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>

                    <Button
                        variant="contained"
                        onClick={() => navigate('/forgot-password')}
                        sx={{ mt: 2 }}
                    >
                        Request New Reset Link
                    </Button>
                </Box>
            </Container>
        );
    }

    return (
        <Container component="main" maxWidth="xs">
            <CssBaseline />
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Box
                    component="img"
                    src={logo}
                    alt="Cardireach Logo"
                    sx={{
                        width: 120,
                        height: 120,
                        objectFit: 'contain',
                        mb: 2
                    }}
                />

                <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
                    Reset Password
                </Typography>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 3, textAlign: 'center' }}>
                    Enter your new password below
                </Typography>

                <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}

                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="newPassword"
                        label="New Password"
                        type="password"
                        id="newPassword"
                        autoComplete="new-password"
                        autoFocus
                        value={newPassword}
                        onChange={(e) => handlePasswordChange(e.target.value)}
                        disabled={isLoading}
                    />

                    {newPassword && (
                        <Box sx={{ mt: 1, mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" color="textSecondary">
                                    Password Strength:
                                </Typography>
                                <Typography variant="caption" color={`${getPasswordStrengthColor()}.main`}>
                                    {getPasswordStrengthLabel()}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={passwordStrength}
                                color={getPasswordStrengthColor()}
                                sx={{ height: 6, borderRadius: 3 }}
                            />
                        </Box>
                    )}

                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="confirmPassword"
                        label="Confirm Password"
                        type="password"
                        id="confirmPassword"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={isLoading}
                    />

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2, py: 1.5 }}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                Resetting Password...
                            </>
                        ) : (
                            'Reset Password'
                        )}
                    </Button>

                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => navigate('/login')}
                            sx={{ cursor: 'pointer' }}
                            type="button"
                        >
                            Back to Login
                        </Link>
                    </Box>
                </Box>
            </Box>
        </Container>
    );
}
